// /////////////////////////////////////////////////////////////
// [ファイル] AssistantGroupDialog.tsx
// [概要] AI アシスタントの「グループ」(定義を束ねる作業文脈の単位) を管理するダイアログ。
//        一覧 (個人/共有・メンバー・定義数) と、作成・編集フォーム (名前・用途・共有メンバー)、
//        削除を提供する。保存・削除は Node 関数 manageAgentGroup 経由 (useAssistantAgents)。
//        定義が残っているグループの削除はバックエンドが拒否する (安全側)。
// /////////////////////////////////////////////////////////////
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../constants/icons';
import LoadingView from './LoadingView';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useMasterData } from '../providers/MasterDataProvider';
import { useStaffNames } from '../hooks/useStaffNames';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import type { AgentGroup, AgentScope, SaveGroupInput } from '../hooks/useAssistantAgents';

interface StaffOption {
  email: string;
  name: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  groups: AgentGroup[];
  loading: boolean;
  // グループ id → 所属する定義の件数 (削除可否の目安として表示)
  agentCountByGroup: Map<string, number>;
  currentUserEmail: string;
  onSave: (input: SaveGroupInput) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

interface FormState {
  id?: string;
  scope: AgentScope;
  name: string;
  description: string;
  members: StaffOption[]; // scope='shared' の共有メンバー (作成者は自動で含まれる)
}

const EMPTY_FORM: FormState = { scope: 'personal', name: '', description: '', members: [] };

export default function AssistantGroupDialog({
  open,
  onClose,
  groups,
  loading,
  agentCountByGroup,
  currentUserEmail,
  onSave,
  onDelete,
}: Props) {
  const notify = useNotification();
  const confirm = useConfirm();
  const { staffList } = useMasterData();
  const { formatMembers, resolve } = useStaffNames();

  // 共有メンバー候補 (MasterDataProvider のスタッフ一覧。staff_id=email)
  const staffOptions = useMemo<StaffOption[]>(
    () =>
      (staffList as { staff_id?: string; staff_name?: string }[])
        .map((s) => ({ email: (s.staff_id ?? '').trim(), name: (s.staff_name ?? '').trim() }))
        .filter((s) => s.email && s.email !== currentUserEmail)
        .sort((a, b) => a.name.localeCompare(b.name, 'ja')),
    [staffList, currentUserEmail]
  );

  // null = 一覧表示 / それ以外 = 作成・編集フォーム
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  // 削除中のグループ id (一覧側にローディングを出す)
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const startCreate = () => setForm({ ...EMPTY_FORM });
  const startEdit = (g: AgentGroup) =>
    setForm({
      id: g.id,
      scope: g.scope,
      name: g.name,
      description: g.description,
      members: g.shared_with
        .filter((e) => e !== g.user_id && e !== currentUserEmail)
        .map((e) => ({ email: e, name: resolve(e) })),
    });

  const handleSave = async () => {
    if (!form) return;
    const name = form.name.trim();
    if (!name) {
      notify.warning('グループ名を入力してください');
      return;
    }
    if (form.scope === 'shared' && form.members.length === 0 && !form.id) {
      notify.warning('共有グループには共有メンバーを 1 人以上指定してください');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        id: form.id,
        scope: form.scope,
        name,
        description: form.description.trim(),
        // 共有時は更新後の全メンバーを渡す (作成者・本人はバックエンドが自動で補完する)
        sharedWith: form.scope === 'shared' ? form.members.map((m) => m.email) : undefined,
      });
      notify.success(form.id ? 'グループを更新しました' : 'グループを作成しました');
      setForm(null);
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'グループの保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (g: AgentGroup) => {
    const count = agentCountByGroup.get(g.id) ?? 0;
    if (count > 0) {
      notify.warning(
        `グループ「${g.name}」には定義が ${count} 件残っています。先に AI に依頼して定義を別グループか共通へ移してください`
      );
      return;
    }
    const ok = await confirm({
      title: 'グループの削除',
      message:
        `グループ「${g.name}」を削除しますか？` +
        (g.scope === 'shared' ? ' 共有メンバーには Chat で通知されます。' : '') +
        ' このグループを選んでいた会話は共通扱いになります。',
      confirmText: '削除する',
      confirmIcon: <Icons.Delete />,
      destructive: true,
    });
    if (!ok) return;
    setDeletingId(g.id);
    try {
      await onDelete(g.id);
      notify.success('グループを削除しました');
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'グループの削除に失敗しました');
    } finally {
      setDeletingId(null);
    }
  };

  const handleClose = () => {
    if (saving || deletingId) return;
    setForm(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          py: 0.75,
          px: 2,
          fontSize: '0.875rem',
          fontWeight: 600,
        }}
      >
        <Icons.Folder sx={{ color: blueGrey[400], fontSize: 18 }} />
        {form ? (form.id ? 'グループを編集' : 'グループを作成') : 'グループ管理'}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#fafafa' }}>
        {saving ? (
          // 保存中はフォームを畳んでローディングに差し替える (manageAgentGroup の往復 + 共有通知の間)
          <LoadingView
            message={form?.id ? 'グループを更新しています...' : 'グループを作成しています...'}
            height="30vh"
            size={40}
          />
        ) : deletingId ? (
          <LoadingView message="グループを削除しています..." height="30vh" size={40} />
        ) : form ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
            <TextField
              autoFocus
              label="グループ名"
              size="small"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              inputProps={{ maxLength: 40 }}
              placeholder="例 会計分析、忘年会運営"
            />
            <TextField
              label="用途 (AI にこのグループの前提として示す 1〜2 文)"
              size="small"
              multiline
              minRows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              inputProps={{ maxLength: 300 }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                共有範囲
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={form.scope}
                onChange={(_, v) => {
                  if (v) setForm({ ...form, scope: v });
                }}
              >
                <ToggleButton value="personal" sx={{ textTransform: 'none', px: 1.5 }}>
                  <Icons.Person sx={{ fontSize: 16, mr: 0.5 }} /> 個人
                </ToggleButton>
                <ToggleButton value="shared" sx={{ textTransform: 'none', px: 1.5 }}>
                  <Icons.Groups sx={{ fontSize: 16, mr: 0.5 }} /> 共有
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {form.scope === 'shared' && (
              <Autocomplete
                multiple
                size="small"
                options={staffOptions}
                value={form.members}
                onChange={(_, v) => setForm({ ...form, members: v })}
                getOptionLabel={(o) => (o.name ? `${o.name} (${o.email})` : o.email)}
                isOptionEqualToValue={(a, b) => a.email === b.email}
                renderInput={(params) => (
                  <TextField {...params} label="共有メンバー (自分は自動で含まれます)" />
                )}
              />
            )}
            <Alert severity="info" sx={{ fontSize: '0.78rem' }}>
              グループに入れた定義 (ルール・スキル・取組み課題) は、このグループを選んだ会話でだけ AI に渡されます。
              共有グループでは中の定義も共有メンバー全員に見えます。
            </Alert>
          </Box>
        ) : loading ? (
          <LoadingView message="グループを読み込んでいます..." height="30vh" size={40} />
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                グループは会話の「作業文脈」の単位です。会話ごとにグループを選ぶと、共通の定義とそのグループの定義だけが AI に渡されます。
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={startCreate}
              startIcon={<Icons.Add />}
              sx={{ ...STANDARD_CTA_BUTTON_SX, mb: 1.5 }}
            >
              新しいグループ
            </Button>
            {groups.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                まだグループがありません
              </Typography>
            ) : (
              groups.map((g) => {
                const count = agentCountByGroup.get(g.id) ?? 0;
                const isCreator = g.user_id === currentUserEmail;
                return (
                  <Box
                    key={g.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: '#fff',
                      px: 1.25,
                      py: 0.75,
                      mb: 0.75,
                      opacity: g.enabled ? 1 : 0.6,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <Tooltip
                          title={
                            g.scope === 'shared' && g.shared_with.length > 0
                              ? `共有メンバー: ${formatMembers(g.shared_with)}`
                              : ''
                          }
                        >
                          <Chip
                            size="small"
                            icon={g.scope === 'shared' ? <Icons.Groups /> : <Icons.Person />}
                            label={g.scope === 'shared' ? `共有 ${g.shared_with.length}人` : '個人'}
                            sx={{ fontSize: '0.65rem', height: 20, flexShrink: 0 }}
                          />
                        </Tooltip>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                          {g.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          定義 {count} 件
                        </Typography>
                        {!isCreator && (
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                            作成者 {resolve(g.user_id)}
                          </Typography>
                        )}
                      </Box>
                      {g.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 0.25, whiteSpace: 'pre-wrap' }}
                        >
                          {g.description}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title="編集">
                      <IconButton size="small" onClick={() => startEdit(g)}>
                        <Icons.Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={count > 0 ? '定義が残っているため削除できません' : '削除'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => void handleDelete(g)}
                          disabled={count > 0}
                          sx={{ color: 'error.main' }}
                        >
                          <Icons.Delete sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                );
              })
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 0.75 }}>
        {form ? (
          <>
            <Button size="small" onClick={() => setForm(null)} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>
              戻る
            </Button>
            <Button
              size="small"
              onClick={() => void handleSave()}
              startIcon={<Icons.Save />}
              disabled={saving || !form.name.trim()}
              sx={STANDARD_CTA_BUTTON_SX}
            >
              保存する
            </Button>
          </>
        ) : (
          <Button size="small" onClick={handleClose} disabled={Boolean(deletingId)} sx={STANDARD_TEXT_BUTTON_SX}>
            閉じる
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
